import { useCallback, useEffect, useRef, useState } from 'react'

const KEY_PRESS_INTERVAL = 500

export const useOpenSearchModal = (isDisabled: boolean) => {
  const [isOpen, setIsOpen] = useState(false)
  const [shiftKeyPresses, setShiftKeyPresses] = useState<number>(0)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const cancelTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      const isShiftKey = event.key === 'Shift'
      setShiftKeyPresses(isShiftKey ? shiftKeyPresses + 1 : 0)

      if (isShiftKey && !shiftKeyPresses) {
        timeoutRef.current = setTimeout(() => {
          setShiftKeyPresses(0)
        }, KEY_PRESS_INTERVAL)
      } else {
        cancelTimeout()
      }
    },
    [cancelTimeout, shiftKeyPresses],
  )

  useEffect(
    function listenToShiftKeyPressesWhenClosed() {
      if (isOpen || isDisabled) {
        return
      }

      document.addEventListener('keyup', handleKeyUp)

      return () => {
        document.removeEventListener('keyup', handleKeyUp)
      }
    },
    [handleKeyUp, isOpen, isDisabled],
  )

  useEffect(() => cancelTimeout, [cancelTimeout])

  useEffect(
    function openOnTwoShiftKeyPresses() {
      if (shiftKeyPresses >= 2) {
        setShiftKeyPresses(0)
        setIsOpen(true)
      }
    },
    [shiftKeyPresses],
  )

  useEffect(
    function resetShiftKeyPressesWhenOpen() {
      if ((isOpen || isDisabled) && !!shiftKeyPresses) {
        setShiftKeyPresses(0)
      }
    },
    [isDisabled, isOpen, shiftKeyPresses],
  )

  return [isOpen, setIsOpen] as const
}
